import { ELITE_RIVE_URL } from '../../../constants'
import type { EliteOnboardingStatusResponse } from '../../../types/misc.types'
import RiveComponent, { RiveFit } from '../../../components/RiveComponent'
import { isYearlyPlanTenure, resolveSelectedPlanForCta } from './NewEliteLandingPlanSelector'
import type { EliteLandingPlan } from '../planTypes'
import { roundOffAndFormatNumberWithCurrency } from '../../../utils/utils'

type NewEliteLandingPriceComparisonSectionProps = {
  eligiblePlans: EliteOnboardingStatusResponse['eligiblePlans']
  selectedPlan: EliteLandingPlan
  selectedPlanDetails?: EliteOnboardingStatusResponse['selectedPlanDetails']
  isStatusLoading?: boolean
}

function resolveSectionPrice(
  eligiblePlans: EliteOnboardingStatusResponse['eligiblePlans'],
  selectedPlan: EliteLandingPlan,
  selectedPlanDetails: EliteOnboardingStatusResponse['selectedPlanDetails'] | undefined
): { amount: number; suffix: 'mo' | 'yr'; planLabel: string } {
  const plan = resolveSelectedPlanForCta(eligiblePlans, selectedPlan)
  if (plan) {
    const tenure = plan?.effectivePlanTenure ?? plan?.basePlanTenure
    const isYearly = isYearlyPlanTenure(tenure)
    return {
      amount: plan?.effectivePlanFees?.amount ?? plan?.basePlanFees?.amount ?? 0,
      suffix: isYearly ? 'yr' : 'mo',
      planLabel: isYearly ? 'On yearly plan' : 'On monthly plan'
    }
  }

  const tenure = selectedPlanDetails?.effectivePlanTenure ?? selectedPlanDetails?.basePlanTenure
  const isYearly = isYearlyPlanTenure(tenure)
  return {
    amount: selectedPlanDetails?.membershipFees?.amount ?? 0,
    suffix: isYearly ? 'yr' : 'mo',
    planLabel: isYearly ? 'On yearly plan' : 'On monthly plan'
  }
}

export default function NewEliteLandingPriceComparisonSection({
  eligiblePlans,
  selectedPlan,
  selectedPlanDetails,
  isStatusLoading
}: NewEliteLandingPriceComparisonSectionProps) {
  if (isStatusLoading) {
    return (
      <div className='pb-12 pt-10 bg-transparent'>
        <div className='flex flex-col items-center gap-12'>
          <div className='flex w-full max-w-[263px] flex-col items-center gap-6'>
            <div className='h-5 w-40 rounded bg-[#E8E8E8] animate-pulse' />
            <div className='h-10 w-48 rounded bg-[#E8E8E8] animate-pulse' />
            <div className='h-3 w-28 rounded bg-[#E8E8E8] animate-pulse' />
          </div>
          <div className='flex w-full flex-col items-center gap-1'>
            <div className='h-4 w-36 rounded bg-[#E8E8E8] animate-pulse' />
            <div className='mt-1 h-[89px] w-full rounded bg-[#E8E8E8] animate-pulse' />
          </div>
        </div>
      </div>
    )
  }

  const { amount: priceAmount, suffix: priceSuffix, planLabel } = resolveSectionPrice(
    eligiblePlans,
    selectedPlan,
    selectedPlanDetails
  )

  return (
    <div style={{ width: 'calc(100% + 40px)', marginLeft: '-20px' }} className='pb-10 pt-[90px]'>
      <div className='flex flex-col items-center gap-12'>
        <div className='flex w-full max-w-[263px] flex-col items-center gap-6 text-center'>
          <p className='text-base font-medium leading-relaxed text-black'>All of this, at just</p>
          <div className='flex flex-col items-center gap-2 py-1 text-[#CC5C2D]'>
            <div className='flex flex-row items-end justify-center'>
              <span className='text-2xl font-medium leading-tight'>
                {roundOffAndFormatNumberWithCurrency(priceAmount)}/{priceSuffix}
              </span>
              <span className='pb-0.5 text-sm font-normal leading-none mb-[2px]'>+GST</span>
            </div>
            <p className='w-full text-[11px] font-normal uppercase leading-relaxed'>{planLabel}</p>
          </div>
        </div>
        <div className='flex w-full flex-col items-center gap-1'>
          <p className='w-full text-center text-[13px] font-normal leading-relaxed text-[#8E9393]'>
            A price that&apos;s less than
          </p>
          <div className='h-[109px] w-full overflow-hidden'>
            <RiveComponent
              src={`${ELITE_RIVE_URL}/marquee.riv`}
              fit={RiveFit.WIDTH}
              stateMachines='animation_loop'
              playOnView
            />
          </div>
        </div>
      </div>
    </div>
  )
}
